import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import '../styles/ticketTheme.css';
import './TicketRules.css';

const FIELD_LABELS = {
  sender_email: 'sender email',
  sender_domain: 'sender domain',
  subject: 'subject',
  body: 'message'
};

const TYPE_LABELS = {
  contains: 'contains',
  equals: 'is exactly',
  starts_with: 'starts with',
  ends_with: 'ends with'
};

const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

const EMPTY_FORM = {
  name: '',
  match_field: 'sender_domain',
  match_type: 'equals',
  match_value: '',
  set_priority: '',
  assign: '', // '' = no change, 'least_busy', or a user id
  add_tag: '',
  set_category: ''
};

const toForm = (rule) => ({
  name: rule.name,
  match_field: rule.match_field,
  match_type: rule.match_type,
  match_value: rule.match_value,
  set_priority: rule.set_priority || '',
  assign: rule.assign_least_busy ? 'least_busy' : (rule.assign_to_user_id ? String(rule.assign_to_user_id) : ''),
  add_tag: rule.add_tag || '',
  set_category: rule.set_category || ''
});

const toPayload = (form) => ({
  name: form.name,
  match_field: form.match_field,
  match_type: form.match_type,
  match_value: form.match_value,
  set_priority: form.set_priority || null,
  assign_least_busy: form.assign === 'least_busy',
  assign_to_user_id: form.assign && form.assign !== 'least_busy' ? Number(form.assign) : null,
  add_tag: form.add_tag,
  set_category: form.set_category
});

function TicketRules() {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id|null, form }
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [pageError, setPageError] = useState('');
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const staffName = (id) => {
    const person = staff.find((s) => String(s.id) === String(id));
    return person ? `${person.first_name} ${person.last_name}` : `user #${id}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/ticketRules');
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      setPageError(err.message || 'Could not load rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      navigate('/login');
      return;
    }
    load();
    apiClient.post('/api/users/findall')
      .then((data) => setStaff(Array.isArray(data) ? data.filter((u) => (u.role_type || '').toLowerCase() !== 'customer') : []))
      .catch(() => setStaff([]));
  }, [navigate, load]);

  const describe = (rule) => {
    const actions = [];
    if (rule.set_priority) actions.push(`priority ${PRIORITY_LABELS[rule.set_priority] || rule.set_priority}`);
    if (rule.assign_least_busy) actions.push('assign to the least busy agent');
    else if (rule.assign_to_user_id) actions.push(`assign to ${staffName(rule.assign_to_user_id)}`);
    if (rule.add_tag) actions.push(`tag "${rule.add_tag}"`);
    if (rule.set_category) actions.push(`category "${rule.set_category}"`);
    return {
      when: `${FIELD_LABELS[rule.match_field] || rule.match_field} ${TYPE_LABELS[rule.match_type] || rule.match_type} "${rule.match_value}"`,
      then: actions.join(', ')
    };
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      const payload = toPayload(editing.form);
      if (editing.id) await apiClient.put(`/api/ticketRules/${editing.id}`, payload);
      else await apiClient.post('/api/ticketRules', payload);
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(err.message || 'Could not save the rule.');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (rule) => {
    setPageError('');
    try {
      await apiClient.put(`/api/ticketRules/${rule.id}`, { enabled: !rule.enabled });
      await load();
    } catch (err) {
      setPageError(err.message || 'Could not change the rule.');
    }
  };

  const move = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= rules.length) return;
    const ids = rules.map((r) => r.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setRules(ids.map((id) => rules.find((r) => r.id === id))); // instant, then confirm with the server
    try {
      await apiClient.put('/api/ticketRules/order', { ids });
    } catch (err) {
      setPageError(err.message || 'Could not save the order.');
      await load();
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/api/ticketRules/${toDelete.id}`);
      setToDelete(null);
      await load();
    } catch (err) {
      setPageError(err.message || 'Could not delete the rule.');
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const setField = (key) => (e) => setEditing((cur) => ({ ...cur, form: { ...cur.form, [key]: e.target.value } }));

  return (
    <PageShell contentClassName="tk">
      <div className="rules-page">
        <div className="rules-header">
          <div>
            <h1>Ticket Rules</h1>
            <p className="rules-intro">
              Rules run on every new ticket that arrives by email, top to bottom. For priority, assignee and category the
              first matching rule wins; tags from every matching rule are added. Put specific rules above general ones.
            </p>
          </div>
          <Button onClick={() => { setFormError(''); setEditing({ id: null, form: EMPTY_FORM }); }}>
            <i className="fas fa-plus" aria-hidden="true"></i> New Rule
          </Button>
        </div>

        {pageError && <p className="rules-error" role="alert">{pageError}</p>}

        {loading ? (
          <p aria-live="polite">Loading rules&hellip;</p>
        ) : rules.length === 0 ? (
          <div className="rules-empty">
            <i className="fas fa-wand-magic-sparkles" aria-hidden="true"></i>
            <p>No rules yet. Create one to set the priority, assignee or tags on tickets from a customer, a domain or a keyword.</p>
          </div>
        ) : (
          <ol className="rules-list">
            {rules.map((rule, index) => {
              const text = describe(rule);
              return (
                <li key={rule.id} className={`rules-item ${rule.enabled ? '' : 'is-off'}`}>
                  <div className="rules-order">
                    <button type="button" aria-label={`Move ${rule.name} up`} disabled={index === 0} onClick={() => move(index, -1)}><i className="fas fa-chevron-up" aria-hidden="true"></i></button>
                    <span className="rules-number">{index + 1}</span>
                    <button type="button" aria-label={`Move ${rule.name} down`} disabled={index === rules.length - 1} onClick={() => move(index, 1)}><i className="fas fa-chevron-down" aria-hidden="true"></i></button>
                  </div>
                  <div className="rules-body">
                    <div className="rules-name">{rule.name}</div>
                    <div className="rules-sentence"><span className="rules-label">When</span> {text.when}</div>
                    <div className="rules-sentence"><span className="rules-label">Then</span> {text.then}</div>
                  </div>
                  <div className="rules-actions">
                    <label className="rules-switch">
                      <input type="checkbox" checked={!!rule.enabled} onChange={() => toggle(rule)} />
                      <span>{rule.enabled ? 'On' : 'Off'}</span>
                    </label>
                    <Button variant="secondary" onClick={() => { setFormError(''); setEditing({ id: rule.id, form: toForm(rule) }); }}>Edit</Button>
                    <Button variant="secondary" onClick={() => setToDelete(rule)} aria-label={`Delete ${rule.name}`}><i className="fas fa-trash" aria-hidden="true"></i></Button>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing && editing.id ? 'Edit Rule' : 'New Rule'}
        size="large"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button type="submit" form="rule-form" disabled={saving}>{saving ? 'Saving…' : 'Save Rule'}</Button>
          </>
        )}
      >
        {editing && (
          <form id="rule-form" onSubmit={save} className="rules-form">
            {formError && <p className="rules-error" role="alert">{formError}</p>}
            <FormField id="rule-name" label="Rule name" required>
              <input id="rule-name" type="text" maxLength={100} placeholder="e.g. VIP customers…" value={editing.form.name} onChange={setField('name')} />
            </FormField>

            <fieldset className="rules-fieldset">
              <legend>When a new email&hellip;</legend>
              <div className="rules-row">
                <FormField id="rule-field" label="Looks at">
                  <select id="rule-field" value={editing.form.match_field} onChange={setField('match_field')}>
                    {Object.entries(FIELD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </FormField>
                <FormField id="rule-type" label="Condition">
                  <select id="rule-type" value={editing.form.match_type} onChange={setField('match_type')}>
                    {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </FormField>
              </div>
              <FormField id="rule-value" label="Text to match" required>
                <input id="rule-value" type="text" maxLength={255} autoComplete="off" spellCheck={false} placeholder="e.g. acme.com or website down…" value={editing.form.match_value} onChange={setField('match_value')} />
              </FormField>
            </fieldset>

            <fieldset className="rules-fieldset">
              <legend>Then&hellip;</legend>
              <div className="rules-row">
                <FormField id="rule-priority" label="Set priority">
                  <select id="rule-priority" value={editing.form.set_priority} onChange={setField('set_priority')}>
                    <option value="">No change</option>
                    {Object.entries(PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </FormField>
                <FormField id="rule-assign" label="Assign to">
                  <select id="rule-assign" value={editing.form.assign} onChange={setField('assign')}>
                    <option value="">No change</option>
                    <option value="least_busy">Least busy agent</option>
                    {staff.map((s) => <option key={s.id} value={String(s.id)}>{s.first_name} {s.last_name}</option>)}
                  </select>
                </FormField>
              </div>
              <div className="rules-row">
                <FormField id="rule-tag" label="Add tag">
                  <input id="rule-tag" type="text" maxLength={60} placeholder="e.g. vip…" value={editing.form.add_tag} onChange={setField('add_tag')} />
                </FormField>
                <FormField id="rule-category" label="Set category">
                  <input id="rule-category" type="text" maxLength={100} placeholder="e.g. Billing…" value={editing.form.set_category} onChange={setField('set_category')} />
                </FormField>
              </div>
              <p className="rules-hint">A rule that sets a priority also resets the ticket&apos;s due date to that priority&apos;s SLA.</p>
            </fieldset>
          </form>
        )}
      </Modal>

      <Modal
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        title="Delete Rule"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setToDelete(null)} disabled={deleting}>Cancel</Button>
            <Button variant="danger" onClick={confirmDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete Rule'}</Button>
          </>
        )}
      >
        <p>Delete the rule <strong>{toDelete && toDelete.name}</strong>? Tickets already created keep what it set.</p>
      </Modal>
    </PageShell>
  );
}

export default TicketRules;
